import React, { useMemo } from 'react';
import {
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  Divider,
  Grid,
  InputAdornment,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  HelpOutline as HelpIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import { guides, getGuide, Guide } from '../../help/guides';

const CATEGORY_ORDER: Guide['category'][] = [
  'Getting started',
  'Sales workflow',
  'Operations',
  'Marketing',
  'Administration',
];

const HelpIndex = () => {
  const navigate = useNavigate();
  const [query, setQuery] = React.useState('');

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return guides;
    return guides.filter(
      g =>
        g.title.toLowerCase().includes(q) ||
        g.summary.toLowerCase().includes(q) ||
        g.content.toLowerCase().includes(q)
    );
  }, [query]);

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1200, mx: 'auto' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
        <HelpIcon sx={{ fontSize: 34, color: 'primary.main' }} />
        <Box>
          <Typography variant="h4" component="h1" fontWeight={600}>
            Help &amp; Guides
          </Typography>
          <Typography variant="body2" color="text.secondary">
            How every process in the CRM works — for new and existing staff.
          </Typography>
        </Box>
      </Box>

      <TextField
        fullWidth
        placeholder="Search guides…"
        value={query}
        onChange={e => setQuery(e.target.value)}
        sx={{ mb: 4 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          ),
        }}
      />

      {filtered.length === 0 && (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">
            No guides match "{query}".
          </Typography>
        </Paper>
      )}

      {CATEGORY_ORDER.map(category => {
        const items = filtered.filter(g => g.category === category);
        if (items.length === 0) return null;
        return (
          <Box key={category} sx={{ mb: 4 }}>
            <Typography variant="h6" sx={{ mb: 2, color: 'text.secondary' }}>
              {category}
            </Typography>
            <Grid container spacing={2}>
              {items.map(guide => (
                <Grid item xs={12} sm={6} md={4} key={guide.slug}>
                  <Card
                    elevation={0}
                    sx={{
                      height: '100%',
                      border: '1px solid',
                      borderColor: 'divider',
                      '&:hover': { borderColor: 'primary.main' },
                    }}
                  >
                    <CardActionArea
                      onClick={() => navigate(`/utilities/help/${guide.slug}`)}
                      sx={{ height: '100%', alignItems: 'flex-start' }}
                    >
                      <CardContent>
                        <Typography variant="h6" component="h2" gutterBottom>
                          {guide.title}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {guide.summary}
                        </Typography>
                      </CardContent>
                    </CardActionArea>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>
        );
      })}
    </Box>
  );
};

const GuideDetail = ({ guide }: { guide: Guide }) => {
  const navigate = useNavigate();
  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 900, mx: 'auto' }}>
      <Button
        startIcon={<BackIcon />}
        onClick={() => navigate('/utilities/help')}
        sx={{ mb: 2 }}
      >
        All guides
      </Button>
      <Paper sx={{ p: { xs: 3, md: 5 } }} elevation={0} variant="outlined">
        <Box sx={{ mb: 3 }}>
          <Chip label={guide.category} size="small" sx={{ mb: 1.5 }} />
          <Typography variant="h4" component="h1" fontWeight={600}>
            {guide.title}
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
            {guide.summary}
          </Typography>
        </Box>
        <Divider sx={{ mb: 3 }} />
        <Box
          sx={{
            '& h2': { mt: 3.5, mb: 1.5, fontSize: '1.4rem', fontWeight: 600 },
            '& h2:first-of-type': { mt: 0 },
            '& p': { mb: 1.5, lineHeight: 1.7 },
            '& ul, & ol': { mb: 1.5, pl: 3 },
            '& li': { mb: 0.5, lineHeight: 1.6 },
            '& blockquote': {
              borderLeft: '4px solid',
              borderColor: 'primary.main',
              pl: 2,
              py: 0.5,
              my: 2,
              ml: 0,
              backgroundColor: 'action.hover',
            },
            '& code': {
              backgroundColor: 'action.hover',
              px: 0.5,
              borderRadius: 0.5,
              fontSize: '0.9em',
            },
          }}
        >
          <ReactMarkdown>{guide.content}</ReactMarkdown>
        </Box>
      </Paper>
    </Box>
  );
};

const HelpPage = () => {
  const { slug } = useParams<{ slug: string }>();

  if (!slug) return <HelpIndex />;

  const guide = getGuide(slug);
  if (!guide) {
    return (
      <Box sx={{ p: 3, maxWidth: 900, mx: 'auto' }}>
        <Typography variant="h5" gutterBottom>Guide not found</Typography>
        <Button startIcon={<BackIcon />} href="/utilities/help">
          All guides
        </Button>
      </Box>
    );
  }
  return <GuideDetail guide={guide} />;
};

export default HelpPage;
